<script setup>
import { computed } from 'vue';
import { compareOptions, matchesOption, periodOf } from './compare-windows';

/**
 * One-tap comparisons: this week against last week, this month against last month, and so on.
 *
 * Each button picks a calendar-aligned window and asks the server to compare it against the
 * previous calendar one, so "last month" really means the month before - not the 31 days that
 * happen to precede it, which is what a length-matched comparison would give.
 */
const props = defineProps({
  modelValue: {
    type: Object,
    required: true,
  },
});

const emit = defineEmits(['update:modelValue']);

const options = computed(() => compareOptions());

/**
 * Whether a button describes the window currently on screen.
 * @param {object} option Option.
 * @returns {boolean}
 */
function isActive(option) {
  return matchesOption(option, props.modelValue);
}

/**
 * Apply a comparison window.
 * @param {object} option Option.
 */
function choose(option) {
  emit('update:modelValue', periodOf(option));
}
</script>

<template>
  <div :class="$style.wrapper">
    <button
      v-for="option in options"
      :key="option.key"
      type="button"
      :class="[$style.button, isActive(option) ? $style.buttonActive : '']"
      @click="choose(option)"
    >
      <span :class="$style.label">{{ option.label }}</span>
      <span :class="$style.caption">{{ option.caption }}</span>
    </button>
  </div>
</template>

<style module>
.wrapper {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
  gap: 8px;
}
.button {
  display: flex;
  flex-direction: column;
  gap: 2px;
  align-items: flex-start;
  border: 1px solid var(--border-color);
  background: white;
  border-radius: var(--border-radius-md);
  padding: 10px 14px;
  cursor: pointer;
  text-align: left;
  font: inherit;
}
.button:hover {
  border-color: var(--pfs-red);
}
.buttonActive {
  background: var(--pfs-red);
  border-color: var(--pfs-red);
}
.label {
  font-size: var(--font-size-lg);
  font-weight: 600;
  color: var(--primary-font-dark-color);
}
.caption {
  font-size: var(--font-size-md);
  color: var(--faint-color);
}
.buttonActive .label,
.buttonActive .caption {
  color: white;
}
</style>
